import { useEffect, useMemo, useRef, useState } from 'react'
import Hls from 'hls.js'
import { Oval } from 'react-loader-spinner'
import { fetchBunnyVideoReadiness } from '../../../../../../api/progressiveUploadService'
import { Message } from '../../../../../../types/api'
import DecryptedMessage from './DecryptedMessage'
import LinkifyText from '../../../../../../shared/LinkifyText'
import { SvgIcon } from '../../../../../../shared/icons'

// Bunny Stream URLs include both the library id and video id. We parse them so
// the player can fall back from HLS playback to the original uploaded file.
const parseBunnyVideoInfo = (src: string) => {
  const match = src.match(/https?:\/\/vz-(.+?)\.b-cdn\.net\/([^/]+)\//i)

  if (!match) {
    return { libraryId: '', videoId: '' }
  }

  return {
    libraryId: match[1],
    videoId: match[2],
  }
}

// Message metadata can arrive as an object from the API or as an array for
// optimistic/multi-file messages, so normalize it before reading thumbnail_url.
const getMessageFileMetadata = (metadata: any) => {
  let normalizedMetadata = Array.isArray(metadata) ? metadata[0] : metadata
  if (typeof normalizedMetadata === 'string') {
    try {
      normalizedMetadata = JSON.parse(normalizedMetadata)
    } catch {
      return {}
    }
  }
  return normalizedMetadata || {}
}

const getMessageThumbnail = (metadata: any) => {
  const normalizedMetadata = getMessageFileMetadata(metadata)
  return normalizedMetadata?.thumbnail_url || ''
}

const isAviUpload = (metadata: any) => {
  const normalizedMetadata = getMessageFileMetadata(metadata)
  const fileName = String(normalizedMetadata?.original_filename || normalizedMetadata?.file_name || '').toLowerCase()
  const mimeType = String(normalizedMetadata?.mime_type || normalizedMetadata?.file_type || '').toLowerCase()

  return fileName.endsWith('.avi') || mimeType === 'video/x-msvideo' || mimeType === 'video/avi'
}

// Detect frames that are effectively black. Some videos have black intro frames
// or the browser may decode an empty-looking frame before content appears.
const isMostlyBlackFrame = (context: CanvasRenderingContext2D, width: number, height: number) => {
  // Sampling a small top-left region keeps this check cheap for large videos.
  const sampleWidth = Math.min(width, 120)
  const sampleHeight = Math.min(height, 120)
  const imageData = context.getImageData(0, 0, sampleWidth, sampleHeight).data
  let darkPixels = 0
  const totalPixels = sampleWidth * sampleHeight

  // RGBA pixels are stored as [r, g, b, a]. Average RGB brightness is enough
  // here; we only need to know whether almost all sampled pixels are dark.
  for (let i = 0; i < imageData.length; i += 4) {
    const brightness = (imageData[i] + imageData[i + 1] + imageData[i + 2]) / 3
    if (brightness < 18) {
      darkPixels += 1
    }
  }

  return darkPixels / totalPixels > 0.9
}

// Generate a thumbnail locally from the playable original video URL. This is
// used when Bunny/CDN returns a thumbnail URL before the image is actually ready.
const generateThumbnailFromVideo = (videoUrl: string): Promise<string> => {
  return new Promise((resolve) => {
    if (!videoUrl) {
      resolve('')
      return
    }

    const video = document.createElement('video')
    let settled = false
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    let seekIndex = 0
    let lastThumbnail = ''

    // Resolve once, clean up the detached video element, and stop the timeout.
    const finish = (thumbnail = '') => {
      if (settled) return
      settled = true

      if (timeoutId) {
        clearTimeout(timeoutId)
      }

      video.removeAttribute('src')
      video.load()
      resolve(thumbnail)
    }

    // Try a few timestamps. The first one is early for fast preview, while the
    // later ones avoid keeping a black frame as the final thumbnail.
    const getSeekTimes = () => {
      const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0

      if (!duration) {
        return [0]
      }

      return Array.from(
        new Set(
          [0.5, 1.5, duration * 0.1, duration * 0.5]
            .filter((time) => time > 0 && time < duration)
            .map((time) => Math.min(time, duration - 0.1)),
        ),
      )
    }

    // Draw the current video frame to a canvas and convert it to a JPEG data URL.
    const captureFrame = () => {
      if (!video.videoWidth || !video.videoHeight) {
        finish()
        return
      }

      try {
        const canvas = document.createElement('canvas')
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight

        const context = canvas.getContext('2d')
        if (!context) {
          finish()
          return
        }

        context.drawImage(video, 0, 0, canvas.width, canvas.height)
        const thumbnail = canvas.toDataURL('image/jpeg', 0.82)
        lastThumbnail = thumbnail

        // Prefer the first non-black frame. If all frames are black, the last
        // captured frame is still better than leaving the poster empty.
        if (!isMostlyBlackFrame(context, canvas.width, canvas.height)) {
          finish(thumbnail)
          return
        }

        const seekTimes = getSeekTimes()
        seekIndex += 1

        if (seekIndex < seekTimes.length) {
          video.currentTime = seekTimes[seekIndex]
          return
        }

        finish(lastThumbnail)
      } catch (error) {
        console.warn('Failed to generate thumbnail from original video:', error)
        finish()
      }
    }

    // Metadata must load before duration and seek operations are reliable.
    const seekForThumbnail = () => {
      try {
        const seekTimes = getSeekTimes()
        seekIndex = 0
        video.currentTime = seekTimes[seekIndex] || 0
      } catch {
        captureFrame()
      }
    }

    // Avoid hanging forever if the original video URL is slow or never decodes.
    timeoutId = setTimeout(() => finish(), 10000)

    // crossOrigin is required for canvas export. If the CDN does not allow CORS,
    // toDataURL will throw and the poster gracefully falls back to empty.
    video.crossOrigin = 'anonymous'
    video.muted = true
    video.playsInline = true
    video.preload = 'metadata'
    video.src = videoUrl
    video.addEventListener('loadedmetadata', seekForThumbnail, { once: true })
    video.addEventListener('seeked', captureFrame, { once: true })
    video.addEventListener('loadeddata', captureFrame, { once: true })
    video.addEventListener('error', () => finish(), { once: true })
    video.load()
  })
}

// A CDN thumbnail URL can exist before the image is actually available. Verify
// the browser can load it before using it as the video poster.
const verifyImage = (imageUrl: string): Promise<boolean> => {
  return new Promise((resolve) => {
    if (!imageUrl) {
      resolve(false)
      return
    }

    const image = new Image()
    // Keep the UI responsive by falling back to local generation quickly.
    const timeoutId = setTimeout(() => resolve(false), 3000)

    image.onload = () => {
      clearTimeout(timeoutId)
      resolve(true)
    }
    image.onerror = () => {
      clearTimeout(timeoutId)
      resolve(false)
    }
    image.src = imageUrl
  })
}

// AVI (and similar) originals are not decodable by browsers, so while Bunny is
// still transcoding we wait on the file service's authoritative video-status
// endpoint instead of hammering the HLS playlist with requests that 404.
// Transcodes of small AVIs have been observed to take well over five minutes,
// so the window is generous: 15s x 120 = 30 minutes per on-screen mount.
const TRANSCODE_POLL_INTERVAL_MS = 15000
const MAX_TRANSCODE_POLLS = 120

const HlsPlayer = ({ message }: { message: Message }) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const hlsRef = useRef<Hls | null>(null)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const retryCountRef = useRef(0)
  const sourceUrl = String(message?.file_url || '')
  const [currentSrc, setCurrentSrc] = useState(sourceUrl)
  const [usedFallback, setUsedFallback] = useState(false)
  const [poster, setPoster] = useState('')
  // Bumps to re-run the playback effect for another HLS attempt while the
  // video is still transcoding on Bunny.
  const [hlsRetryToken, setHlsRetryToken] = useState(0)
  // AVI HLS messages start in the processing state so a dead player never
  // flashes; MANIFEST_PARSED clears it immediately when the video is ready.
  const [isAwaitingTranscode, setIsAwaitingTranscode] = useState(
    () => isAviUpload(message?.metadata) && String(message?.file_url || '').endsWith('.m3u8'),
  )
  // Bunny reported the transcode as errored (status 5/6) — permanent for this video.
  const [transcodeFailed, setTranscodeFailed] = useState(false)

  // Build the original Bunny file URL. HLS is preferred for playback, but the
  // original URL is useful for fallback playback and thumbnail generation.
  const { libraryId, videoId } = useMemo(() => parseBunnyVideoInfo(sourceUrl), [sourceUrl])
  const originalSrc = useMemo(() => {
    if (!libraryId || !videoId) return ''
    return `https://vz-${libraryId}.b-cdn.net/${videoId}/original`
  }, [libraryId, videoId])
  const cdnThumbnailUrl = useMemo(() => getMessageThumbnail(message.metadata), [message.metadata])
  const isAvi = useMemo(() => isAviUpload(message.metadata), [message.metadata])
  const isLocalAviPreview = sourceUrl.startsWith('blob:') && isAvi

  // Reset local playback/poster state whenever this component receives a new
  // video URL, such as optimistic blob URL -> real API URL.
  useEffect(() => {
    setCurrentSrc(sourceUrl)
    setUsedFallback(false)
    setPoster('')
    setIsAwaitingTranscode(isAvi && sourceUrl.endsWith('.m3u8'))
    setTranscodeFailed(false)
    setHlsRetryToken(0)
    retryCountRef.current = 0
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }
  }, [sourceUrl, isAvi])

  // The original file of an AVI upload can never play in the browser, so a
  // failed HLS attempt means the transcode is not done: show the processing
  // card and wait on the authoritative Bunny status (via the file service)
  // instead of hammering the playlist with requests that 404. Another HLS
  // attempt is made only once Bunny reports the video ready; an errored
  // transcode (status 5/6) becomes a permanent failed state.
  const scheduleTranscodePoll = () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current)
    }
    if (retryCountRef.current >= MAX_TRANSCODE_POLLS) return
    retryCountRef.current += 1
    setIsAwaitingTranscode(true)

    retryTimerRef.current = setTimeout(async () => {
      retryTimerRef.current = null
      const readiness = videoId ? await fetchBunnyVideoReadiness(videoId) : null

      if (readiness === 'failed') {
        setTranscodeFailed(true)
        return
      }

      if (readiness === 'ready') {
        // Re-run the playback effect for a fresh HLS attempt. If the playlist
        // still fails (CDN propagation), the error handler lands back here and
        // the next poll is again a full interval away — no tight loop.
        setHlsRetryToken((token) => token + 1)
        return
      }

      // Still processing (or a transient status-endpoint error): keep waiting.
      scheduleTranscodePoll()
    }, TRANSCODE_POLL_INTERVAL_MS)
  }

  // Poster selection order:
  // 1. Use CDN thumbnail if it can actually load.
  // 2. Otherwise generate a thumbnail from the original video.
  // 3. Leave poster empty if both options fail.
  useEffect(() => {
    let cancelled = false

    const resolvePoster = async () => {
      // While Bunny is still transcoding, the processing card is shown and the
      // CDN thumbnail does not exist yet — probing it every poll only produces
      // request noise. The effect re-runs when the transcode completes.
      if (isAwaitingTranscode || transcodeFailed) return

      if (cdnThumbnailUrl) {
        const isCdnThumbnailReady = await verifyImage(cdnThumbnailUrl)

        if (cancelled) return

        if (isCdnThumbnailReady) {
          setPoster(cdnThumbnailUrl)
          return
        }
      }

      // The original file of an AVI upload cannot be decoded by the browser,
      // so generating a local thumbnail from it can never work.
      if (isAvi) return

      const generatedThumbnail = await generateThumbnailFromVideo(originalSrc)

      if (!cancelled && generatedThumbnail) {
        setPoster(generatedThumbnail)
      }
    }

    resolvePoster()

    return () => {
      cancelled = true
    }
  }, [cdnThumbnailUrl, originalSrc, isAvi, isAwaitingTranscode, transcodeFailed])

  // Attach the selected video source to the DOM video element. HLS streams use
  // hls.js where supported; non-HLS URLs are assigned directly to video.src.
  useEffect(() => {
    if (!currentSrc || !videoRef.current) return

    const video = videoRef.current

    // Destroy any previous HLS instance before attaching a new source.
    if (hlsRef.current) {
      hlsRef.current.destroy()
      hlsRef.current = null
    }

    if (currentSrc.endsWith('.m3u8')) {
      // Chrome/Firefox need hls.js for .m3u8 playback.
      if (Hls.isSupported()) {
        const hls = new Hls()
        hlsRef.current = hls
        hls.loadSource(currentSrc)
        hls.attachMedia(video)

        // The playlist parsed, so transcoding is done and HLS playback works.
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setIsAwaitingTranscode(false)
        })

        hls.on(Hls.Events.ERROR, (_, data) => {
          if (!data?.fatal) return

          // An AVI original can't play in any browser, so a missing playlist
          // means Bunny is still transcoding — keep the processing card up and
          // wait on the Bunny status, rather than switching to the unplayable
          // original file.
          if (isAvi) {
            hls.destroy()
            hlsRef.current = null
            scheduleTranscodePoll()
            return
          }

          // If the HLS playlist is not ready or fails fatally, try Bunny's
          // original file URL once before giving up.
          if (!usedFallback && originalSrc) {
            hls.destroy()
            hlsRef.current = null
            setUsedFallback(true)
            setCurrentSrc(originalSrc)
            return
          }

          console.error('HLS fatal error:', data)
        })

        return () => {
          hls.destroy()
          hlsRef.current = null
        }
      }

      // Safari can play HLS natively without hls.js.
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = currentSrc

        const onLoadedMetadata = () => {
          setIsAwaitingTranscode(false)
        }

        const onError = () => {
          if (isAvi) {
            scheduleTranscodePoll()
            return
          }

          if (!usedFallback && originalSrc) {
            setUsedFallback(true)
            setCurrentSrc(originalSrc)
          }
        }

        video.addEventListener('loadedmetadata', onLoadedMetadata)
        video.addEventListener('error', onError)

        return () => {
          video.removeEventListener('loadedmetadata', onLoadedMetadata)
          video.removeEventListener('error', onError)
        }
      }
    }

    // Direct playback path for original files, blob URLs, and non-HLS formats.
    video.src = currentSrc

    return () => {
      video.removeAttribute('src')
      video.load()
    }
  }, [currentSrc, originalSrc, usedFallback, isAvi, hlsRetryToken])

  // Stop any pending transcode retry when the player unmounts.
  useEffect(() => {
    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current)
        retryTimerRef.current = null
      }
    }
  }, [])

  if (!sourceUrl) return null

  const isUploading = message.uploadStatus === 'uploading'
  const showProcessingCard = isLocalAviPreview || isAwaitingTranscode

  return (
    <div className="message-video" style={{ position: 'relative' }}>
      {/* Upload progress overlay */}
      {isUploading && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0, 0, 0, 0.7)',
            borderRadius: '8px',
            zIndex: 10,
          }}
        >
          <Oval
            visible={true}
            height="50"
            width="50"
            ariaLabel="Loading"
            wrapperClass="loader-wrapper"
            color="#0066FF"
          />
        </div>
      )}

      {/* Failed upload overlay */}
      {message.uploadStatus === 'failed' && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(220, 53, 69, 0.9)',
            borderRadius: '8px',
            zIndex: 10,
            color: '#fff',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          Upload Failed
        </div>
      )}

      {showProcessingCard && (
        <div
          className={`chat-media-video avi-video-preview${transcodeFailed ? ' avi-video-preview--failed' : ''}`}
          role="status"
          aria-label={transcodeFailed ? 'AVI video processing failed' : 'AVI video is processing'}
        >
          <div className="avi-video-preview__content">
            <div className="avi-video-preview__icon" aria-hidden="true">
              <SvgIcon iconId="file-video" />
            </div>
            <div className="avi-video-preview__details">
              <span className="avi-video-preview__format">AVI VIDEO</span>
              <strong>{transcodeFailed ? 'Processing failed' : 'Preparing preview'}</strong>
              <small>
                {transcodeFailed
                  ? 'This video could not be processed.'
                  : 'Your video will be ready to play shortly.'}
              </small>
            </div>
          </div>
          <span className="avi-video-preview__status">
            <span className="avi-video-preview__status-dot" />
            {transcodeFailed ? 'Failed' : 'Processing'}
          </span>
        </div>
      )}
      {/* The video element stays mounted (hidden) behind the processing card so
          transcode retries can keep attaching to it; it is revealed as soon as
          the HLS playlist becomes playable. Local blob previews of AVI files
          can never play, so no video element is needed for them. */}
      {!isLocalAviPreview && (
        <video
          ref={videoRef}
          controls
          playsInline
          className="chat-media-video"
          preload="metadata"
          // Empty poster is acceptable; the browser will render the video element
          // normally while poster generation/CDN verification is pending or failed.
          poster={poster}
          onError={() => console.warn('Video failed to load:', currentSrc)}
          style={{
            opacity: isUploading ? 0.5 : 1,
            display: showProcessingCard ? 'none' : undefined,
          }}
        />
      )}
      {message.content && (
        <h5>
          <DecryptedMessage message={message}>
            {(decryptedContent) => <LinkifyText text={decryptedContent} />}
          </DecryptedMessage>
        </h5>
      )}
    </div>
  )
}

export default HlsPlayer
